import { ApiError } from '../../services/api';
import type { PackagePreviewError } from '../../types/packages';
import { packageItemField, type PackageField, type PackageFieldErrors } from './packageUtils';
import { reasonText, type PackagesT } from './packagesI18n';

/** Codes that belong to one field (design §3 PackageErrorCode). */
const CODE_TO_FIELD: Record<string, { field: PackageField; key: string }> = {
  PACKAGE_ITEMS_COUNT: { field: 'items', key: 'packages.err.itemsCount' },
  PACKAGE_PRICE_NOT_BELOW_TOTAL: { field: 'packagePrice', key: 'packages.err.priceNotBelowTotal' },
  PACKAGE_PERCENT_RANGE: { field: 'discountPercent', key: 'packages.err.percentRange' },
  PACKAGE_WINDOW_INVALID: { field: 'endsAt', key: 'packages.err.window' },
  CONTACT_DETAILS_IN_TEXT: { field: 'nameEn', key: 'packages.err.contactDetails' },
  INVALID_STORAGE_KEY: { field: 'cover', key: 'packages.err.storageKey' },
};

/** Codes shown as one message above the actions. */
const CODE_TO_FORM: Record<string, string> = {
  PACKAGE_LIMIT_REACHED: 'packages.err.limitReached',
  PACKAGE_STATE_CHANGED: 'packages.err.stateChanged',
  PACKAGE_INVALID_TRANSITION: 'packages.err.transition',
  PACKAGE_UNAVAILABLE: 'packages.err.unavailable',
  PACKAGE_NOT_FOUND: 'packages.err.notFound',
  NOT_YOUR_GARAGE: 'packages.err.forbidden',
};

/** Bean-validation field names → form fields. `items[3]…` lands on that item's row. */
const SERVER_FIELD: Record<string, PackageField> = {
  nameEn: 'nameEn', nameAr: 'nameAr', descriptionEn: 'descriptionEn', descriptionAr: 'descriptionAr',
  coverStorageKey: 'cover', items: 'items', packagePrice: 'packagePrice', discountPercent: 'discountPercent',
  startsAt: 'startsAt', endsAt: 'endsAt',
};
const ITEM_FIELD_RE = /^items\[(\d+)]/;

export interface MappedPackageError {
  fieldErrors: PackageFieldErrors;
  formError: string | null;
  code: string | null;
}

export const isKnownPackageCode = (c: string | null | undefined): c is string => !!c && (c in CODE_TO_FIELD || c in CODE_TO_FORM || c === 'PACKAGE_ITEM_INELIGIBLE');

/** R3 copy: "<Service> can't be added: <reason>". */
export function ineligibleMessage(t: PackagesT, name: string, reason: string | null | undefined): string {
  return t('packages.err.itemIneligible', { name: name || '—', reason: reasonText(t, reason) });
}

/**
 * Maps an API failure ({error, code, message, details}) to translated field errors and/or one form message.
 * `itemIds` is the request's item order (for `items[i]` field errors); `nameOf` names a service for R3.
 */
export function mapPackageError(err: unknown, t: PackagesT, itemIds: string[], nameOf: (offeringId: string) => string): MappedPackageError {
  const out: MappedPackageError = { fieldErrors: {}, formError: null, code: null };
  if (!(err instanceof ApiError)) {
    out.formError = t('packages.err.generic');
    return out;
  }
  const code = isKnownPackageCode(err.errorCode) ? err.errorCode : isKnownPackageCode(err.code) ? err.code : null;
  out.code = code ?? err.errorCode ?? err.code ?? null;

  for (const [serverField, value] of Object.entries(err.fieldErrors ?? {})) {
    const itemMatch = ITEM_FIELD_RE.exec(serverField);
    const field: PackageField | undefined = itemMatch
      ? (itemIds[Number(itemMatch[1])] ? packageItemField(itemIds[Number(itemMatch[1])]) : 'items')
      : SERVER_FIELD[serverField];
    if (!field) continue;
    const valueCode = typeof value === 'string' && CODE_TO_FIELD[value] ? value : null;
    out.fieldErrors[field] = valueCode ? t(CODE_TO_FIELD[valueCode].key) : (typeof value === 'string' && value ? value : t('packages.err.generic'));
  }

  if (code === 'PACKAGE_ITEM_INELIGIBLE') {
    const offeringId = typeof err.details?.offeringId === 'string' ? err.details.offeringId : null;
    const reason = typeof err.details?.reason === 'string' ? err.details.reason : null;
    const message = ineligibleMessage(t, offeringId ? nameOf(offeringId) : '', reason);
    out.fieldErrors[offeringId ? packageItemField(offeringId) : 'items'] = message;
  } else if (code && CODE_TO_FIELD[code]) {
    const { field, key } = CODE_TO_FIELD[code];
    // CONTACT_DETAILS_IN_TEXT may already name its field in `errors`.
    if (Object.keys(out.fieldErrors).length === 0) out.fieldErrors[field] = t(key);
  } else if (code && CODE_TO_FORM[code]) {
    out.formError = t(CODE_TO_FORM[code]);
  } else if (Object.keys(out.fieldErrors).length === 0) {
    out.formError = err.status === 403 ? t('packages.err.forbidden') : (err.message || t('packages.err.generic'));
  }
  return out;
}

/** One message for list actions and confirm dialogs (no fields there). */
export function packageActionErrorMessage(err: unknown, t: PackagesT): string {
  const m = mapPackageError(err, t, [], () => '');
  return m.formError ?? Object.values(m.fieldErrors)[0] ?? t('packages.err.generic');
}

/** The preview's non-throwing errors[] → inline messages (R2–R4) on the picker rows and the price box. */
export function previewErrorsToFields(errors: PackagePreviewError[] | undefined, t: PackagesT, nameOf: (offeringId: string) => string,
  reasonOf: (offeringId: string) => string | null): PackageFieldErrors {
  const out: PackageFieldErrors = {};
  for (const e of errors ?? []) {
    if (e.code === 'PACKAGE_ITEM_INELIGIBLE' && e.offeringId) {
      out[packageItemField(e.offeringId)] = ineligibleMessage(t, nameOf(e.offeringId), reasonOf(e.offeringId));
    } else if (CODE_TO_FIELD[e.code] && !out[CODE_TO_FIELD[e.code].field]) {
      out[CODE_TO_FIELD[e.code].field] = t(CODE_TO_FIELD[e.code].key);
    }
  }
  return out;
}
